import { useEffect, useState, useCallback } from "react";
import { Lock, Unlock, Trash2, History, Loader2 } from "lucide-react";
import { listResolutionAuditLog } from "@/lib/api";
import { formatIndianDate } from "@/lib/date";

const ICON_FOR_ACTION = {
  locked: { icon: Lock, color: "text-amber-700", bg: "bg-amber-50 border-amber-300" },
  unlocked: { icon: Unlock, color: "text-zinc-700", bg: "bg-zinc-50 border-zinc-300" },
  deleted: { icon: Trash2, color: "text-red-700", bg: "bg-red-50 border-red-300" },
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = formatIndianDate(iso);
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${date} · ${time}`;
  } catch {
    return iso.slice(0, 19);
  }
}

/**
 * Append-only audit history for a single resolution. Shows lock/unlock/delete
 * events with actor + timestamp + optional note. Newest first.
 * @param {string} resolutionId
 * @param {number} refreshKey — bump this to force a refetch (e.g. after lock action)
 */
export default function ResolutionAuditLog({ resolutionId, refreshKey = 0 }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!resolutionId) return;
    setLoading(true);
    try {
      const r = await listResolutionAuditLog(resolutionId);
      setEntries(r.data || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [resolutionId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Auto-expand when there's interesting activity (at least one lock event)
  useEffect(() => {
    if (entries.length > 0 && entries.some((e) => e.action === "locked")) {
      setOpen(true);
    }
  }, [entries]);

  if (loading && entries.length === 0) {
    return (
      <section className="border border-zinc-200 bg-white mt-8" data-testid="audit-section">
        <div className="px-5 py-3 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 size={12} className="animate-spin" /> Loading lock history…
        </div>
      </section>
    );
  }

  if (entries.length === 0) {
    return null; // no audit events yet — keep UI quiet
  }

  return (
    <section className="border border-zinc-200 bg-white mt-8" data-testid="audit-section">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-zinc-50 text-left"
        data-testid="audit-toggle"
      >
        <div className="flex items-center gap-2">
          <History size={14} className="text-[#002FA7]" />
          <span className="label-eyebrow">Lock history ({entries.length})</span>
        </div>
        <span className="text-[11px] text-zinc-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <ol className="divide-y divide-zinc-100 border-t border-zinc-100" data-testid="audit-entries">
          {entries.map((e) => {
            const meta = ICON_FOR_ACTION[e.action] || { icon: History, color: "text-zinc-600", bg: "bg-zinc-50 border-zinc-300" };
            const Icon = meta.icon;
            const note = e?.details?.note || "";
            return (
              <li key={e.id} className="px-5 py-3 flex items-start gap-3" data-testid={`audit-row-${e.id}`}>
                <span className={`inline-flex items-center justify-center w-7 h-7 border ${meta.bg} ${meta.color} shrink-0 mt-0.5`}>
                  <Icon size={12} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-900">
                    <span className="font-medium capitalize">{e.action}</span>
                    {" by "}
                    <span className="font-medium">{e.actor_name || e.actor_email || "Unknown user"}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{formatWhen(e.created_at)}</div>
                  {note && (
                    <div className="text-[12px] text-zinc-700 mt-1 italic">“{note}”</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
